import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Button, { ButtonToneProps } from '../components/ui/Button/Button';
import Select, { Option } from '../components/ui/Select/Select';
import Textarea from '../components/ui/Textarea/Textarea';
import FileUpload from '../components/ui/FileUpload/FileUpload';
import Icon, { IconName } from '../components/ui/Icon/Icon';
import Badge, { BadgeType } from '../components/ui/Badge/Badge';
import Divider from '../components/ui/Divider/Divider';
import Chat from '../components/layouts/Chat/Chat';
import { Attachment } from '../components/layouts/Chat/Attachment';
import ContainerBody from '../components/ui/ContainerOverlay/ContainerBody/ContainerBody';
import { RouteOverlay } from '../components/layouts/RouteOverlay/RouteOverlay';
import { OverlaySectionHeadline } from '../components/layouts/OverlaySectionHeadline/OverlaySectionHeadline';
import LinkedReturnsTable, { LinkedReturn } from '../components/layouts/LinkedReturnsTable/LinkedReturnsTable';
import { FileObject } from '../interfaces/shared';
import { CaseDescription } from './CaseOverlayContent';
import { getFormattedDay } from './CasesOverview';
import {
  ClaimDocumentIn,
  ClaimType,
  claimOutcome,
  claimStatusLabelKey,
  useClaimMutations,
  useClaimQuery,
  useEligibleOrdersQuery,
} from '../services/claim';

const CLAIM_TYPES: ClaimType[] = ['clarification', 'damage'];
const MIN_DESCRIPTION = 10;

const STATUS_BADGE: Record<string, BadgeType> = {
  submitted: BadgeType.Neutral,
  in_progress: BadgeType.Warning,
  documents_missing: BadgeType.Error,
  at_carrier: BadgeType.Warning,
  credit_issued: BadgeType.Successful,
  resolved: BadgeType.Successful,
  closed: BadgeType.Successful,
  rejected: BadgeType.Error,
};

type ClaimOverlayProps = {
  mode: 'view' | 'create';
};

type IconTableRowProps = {
  icon: IconName;
  label: React.ReactNode;
  content: React.ReactNode;
};

const IconTable = ({ rows }: { rows: IconTableRowProps[] }) => (
  <div className="grid grid-cols-[min-content_minmax(min-content,156px)_1fr] gap-16">
    {rows
      .filter(row => row.content)
      .map(row => (
        <React.Fragment key={`${row.icon}-${String(row.label)}`}>
          <Icon name={row.icon} className="text-faded" />
          <div className="typo-text-14--bold text-faded pt-2">{row.label}</div>
          <div className="typo-text-14 text-full pt-2">{row.content}</div>
        </React.Fragment>
      ))}
  </div>
);

const toDocuments = (files: FileObject[], documentType: ClaimDocumentIn['document_type']): ClaimDocumentIn[] =>
  files.map(f => ({
    document_type: documentType,
    name: f.name,
    file: f.file || '',
    mimetype: f.mimetype,
  }));

function InfoCallout({
  tone = 'info',
  icon,
  children,
}: {
  tone?: 'warning' | 'info' | 'success';
  icon: IconName;
  children: React.ReactNode;
}) {
  const TONE_CLASSES = {
    warning: 'status-warning-surface status-warning-full',
    success: 'status-success-surface status-success-full',
    info: 'surface-base text-full border border-subtle',
  } as const;
  const toneClasses = TONE_CLASSES[tone] ?? TONE_CLASSES.info;
  return (
    <div className={`flex items-start gap-12 rounded-base p-16 ${toneClasses}`}>
      <Icon name={icon} width={20} height={20} className="shrink-0 mt-2" />
      <div className="typo-text-14 flex flex-col gap-8">{children}</div>
    </div>
  );
}

function ClaimCreateForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { createMutation } = useClaimMutations();
  const [orderSearch, setOrderSearch] = useState('');
  const { data: eligibleOrders } = useEligibleOrdersQuery(orderSearch);

  const presetType = searchParams.get('type');
  const presetOrderId = searchParams.get('order');
  const presetOrderName = searchParams.get('orderName') ?? '';
  const orderLocked = !!presetOrderId;

  const [claimType, setClaimType] = useState<ClaimType>(
    presetType === 'damage' || presetType === 'clarification' ? (presetType as ClaimType) : 'clarification',
  );
  const [saleOrderId, setSaleOrderId] = useState<number | null>(presetOrderId ? Number(presetOrderId) : null);
  const [description, setDescription] = useState('');
  const [salesInvoiceFiles, setSalesInvoiceFiles] = useState<FileObject[]>([]);
  const [goodsValueProofFiles, setGoodsValueProofFiles] = useState<FileObject[]>([]);
  const [otherFiles, setOtherFiles] = useState<FileObject[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const isDamage = claimType === 'damage';
  const orders = (eligibleOrders as any[]) ?? [];

  // Only a damage claim requires documents (sales invoice + goods-value proof).
  const valid =
    description.trim().length >= MIN_DESCRIPTION &&
    (!isDamage || (salesInvoiceFiles.length > 0 && goodsValueProofFiles.length > 0));

  const submit = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      const documents: ClaimDocumentIn[] = [
        ...(isDamage ? toDocuments(salesInvoiceFiles, 'sales_invoice') : []),
        ...(isDamage ? toDocuments(goodsValueProofFiles, 'goods_value_proof') : []),
        ...toDocuments(otherFiles, 'other'),
      ];
      const response = await createMutation.mutateAsync({
        body: {
          claim_type: claimType,
          sale_order_id: saleOrderId,
          description,
          documents,
        },
      });
      toast.success(t('claims.created_toast', { number: (response as any)?.name, defaultValue: 'Claim created.' }));
      navigate(`/cases/claims/${(response as any)?.id}`);
    } catch (error) {
      const detail = String((error as any)?.detail ?? '');
      toast.error(
        detail.includes('CLAIM_ALREADY_EXISTS')
          ? t('claims.create_failed_existing_claim', {
              defaultValue: 'Für diese Sendung existiert bereits eine Meldung.',
            })
          : t('claims.create_failed', { defaultValue: 'Could not create the claim.' }),
      );
      // eslint-disable-next-line no-console
      console.error('Failed to create claim:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const label = (key: string, fallback: string) => t(key, { defaultValue: fallback });

  const typeOptions: Option[] = CLAIM_TYPES.map(type => ({
    value: type,
    label: label(`claims.type_${type}`, type),
  }));
  const orderOptions: Option[] = orders.map(order => {
    const parts = [order.id, order.name, order.customer_name, order.tracking_number].filter(Boolean);
    const blockedNote = order.has_claim
      ? ` — ${label('claims.order_has_claim', 'Meldung bereits vorhanden')}${
          order.claim_name ? ` (${order.claim_name})` : ''
        }`
      : '';
    return {
      value: String(order.id),
      label: `${parts.join(' · ')}${blockedNote}`,
      isDisabled: !!order.has_claim,
    };
  });

  return (
    <div className="flex flex-col gap-24 max-w-[640px]">
      <div className="flex flex-col gap-12">
        <h2 className="typo-title-36 text-full">{label('claims.create_title', 'Create claim')}</h2>
        <p className="typo-text-14 text-faded">
          {label('claims.create_subtitle', 'Briefly describe your concern — we will add details together if needed.')}
        </p>
      </div>

      {isDamage && (
        <InfoCallout tone="warning" icon="warning">
          <span>
            {label(
              'claims.damage_deadline_info',
              'A damage claim can only be filed within 6 working days of the delivery date per the tracking link.',
            )}
          </span>
        </InfoCallout>
      )}

      {!orderLocked && (
        <div className="flex flex-col gap-8">
          <span className="typo-text-14--bold text-faded">{label('claims.field_type', 'Type')}</span>
          <Select
            options={typeOptions}
            defaultValue={typeOptions.find(o => o.value === claimType)}
            onChange={(option: Option) => option && setClaimType(option.value as ClaimType)}
          />
        </div>
      )}

      {orderLocked ? (
        <div className="flex flex-col gap-8">
          <span className="typo-text-14--bold text-faded">{label('claims.field_order', 'Outbound')}</span>
          <span className="typo-text-14 text-full">{presetOrderName}</span>
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          <span className="typo-text-14--bold text-faded">{label('claims.field_order', 'Outbound')}</span>
          <span className="typo-text-12 text-faded">
            {label('claims.field_order_hint', 'Select the affected outbound — we derive the tracking number from it.')}
          </span>
          <Select
            options={orderOptions}
            isClearable
            isSearchable
            disableLocalFilter
            onInputChange={setOrderSearch}
            placeholder={label('claims.field_order_search_placeholder', 'Search by order, customer or tracking…')}
            onChange={(option: Option) => setSaleOrderId(option ? Number(option.value) : null)}
          />
        </div>
      )}

      <div className="flex flex-col gap-8">
        <span className="typo-text-14--bold text-faded">
          {label('claims.field_description', 'What is this about?')}
        </span>
        <span className="typo-text-12 text-faded">
          {label('claims.field_description_hint', 'Short description of your concern (min. 10 characters).')}
        </span>
        <Textarea value={description} onChange={e => setDescription(e.target.value)} />
        <span className={`typo-text-12 ${description.trim().length >= MIN_DESCRIPTION ? 'text-faded' : 'text-error'}`}>
          {description.trim().length}/{MIN_DESCRIPTION}
        </span>
      </div>

      <div className="flex flex-col gap-12">
        <span className="typo-text-14--bold text-faded">{label('claims.docs_title', 'Documents')}</span>
        {isDamage && (
          <>
            <span className="typo-text-12 text-faded">
              {label('claims.docs_mandatory_hint', 'A damage claim requires a sales invoice and a goods-value proof.')}
            </span>
            <InfoCallout tone="info" icon="info">
              <span>
                {label(
                  'claims.damage_photos_info',
                  'As many photos as possible of the inner and outer packaging and of the damaged products are mandatory. If you do not have photos yet, please ask the parcel recipient as soon as possible and upload them afterwards.',
                )}
              </span>
              <span>
                {label(
                  'claims.damage_photos_info2',
                  'We will still file the damage claim with the carrier in the meantime, but without photos no further processing is possible.',
                )}
              </span>
            </InfoCallout>
            <div className="typo-text-14 flex flex-col gap-4">
              <span className="text-faded">{label('claims.doc_sales_invoice', 'Sales invoice')} *</span>
              <FileUpload onChange={setSalesInvoiceFiles} />
            </div>
            <div className="typo-text-14 flex flex-col gap-4">
              <span className="text-faded">{label('claims.doc_goods_value_proof', 'Goods-value proof')} *</span>
              <FileUpload onChange={setGoodsValueProofFiles} />
            </div>
          </>
        )}
        <div className="typo-text-14 flex flex-col gap-4">
          <span className="text-faded">{label('claims.doc_other', 'Other documents or images')}</span>
          <FileUpload onChange={setOtherFiles} />
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          title={label('claims.submit', 'Create claim')}
          icon="checkmark"
          tone={ButtonToneProps.PRIMARY_BLUE}
          disabled={!valid || submitting}
          onClick={submit}
        />
      </div>
    </div>
  );
}

function ClaimDetail({ id }: { id: number }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: claim } = useClaimQuery(id);
  const label = (key: string, fallback: string) => t(key, { defaultValue: fallback });
  if (!claim) return null;

  const c = claim as any;
  const orderLink = c.sale_order?.id ? `/outbound/${c.sale_order.id}` : null;
  const rawReturnLinks: LinkedReturn[] = c.return_links ?? [];
  const returnLinks: LinkedReturn[] =
    rawReturnLinks.length || !c.return_reference ? rawReturnLinks : [{ reference: c.return_reference }];

  const rows: IconTableRowProps[] = [
    {
      icon: 'note',
      label: label('claims.col_type', 'Type'),
      content: (
        <Badge
          className="max-w-min"
          label={label(`claims.type_${c.claim_type}`, c.claim_type)}
          expanded
          type={BadgeType.Neutral2}
        />
      ),
    },
    {
      icon: 'labelImportant',
      label: label('claims.col_status', 'Status'),
      content: (
        <Badge
          className="max-w-min"
          label={label(claimStatusLabelKey(c.claim_type, c.status), c.status)}
          expanded
          type={STATUS_BADGE[c.status] ?? BadgeType.Neutral}
        />
      ),
    },
    { icon: 'package', label: label('claims.col_tracking', 'Tracking number'), content: c.tracking_number },
    { icon: 'local-shipping', label: label('claims.col_carrier', 'Carrier'), content: c.carrier },
    {
      icon: 'link',
      label: label('claims.field_order', 'Outbound'),
      content: orderLink ? (
        <Link className="underline" to={orderLink}>
          {c.sale_order?.name}
        </Link>
      ) : (
        c.sale_order?.name
      ),
    },
    { icon: 'accountCircle', label: t('case.assigned_to'), content: c.assigned_user_display },
    { icon: 'warehouse', label: label('claims.col_warehouse', 'Lager'), content: c.warehouse_name },
    { icon: 'note', label: label('claims.col_carrier_reference', 'Carrier-Referenz'), content: c.carrier_reference },
    { icon: 'note', label: label('claims.col_carrier_ticket', 'Carrier-Ticket-Nr.'), content: c.carrier_ticket_number },
    {
      icon: 'note',
      label: label('claims.col_recorded_at', 'Erfassung'),
      content: c.recorded_at ? getFormattedDay(new Date(c.recorded_at)) : null,
    },
  ];

  // For a closed claim, surface whether it was accepted or rejected (and how).
  const outcome = claimOutcome(c.status, c.resolution_type);
  const outcomeText = c.clarification_outcome
    ? label(`claims.clarification_outcome_${c.clarification_outcome}`, c.clarification_outcome)
    : outcome
      ? label(`claims.outcome_${outcome}`, outcome)
      : '';
  if (outcome) {
    rows.push({
      icon: outcome === 'accepted' ? 'checkmark' : 'close',
      label: label('claims.outcome', 'Outcome'),
      content: (
        <Badge
          className="max-w-min"
          label={outcomeText}
          expanded
          type={outcome === 'accepted' ? BadgeType.Successful : BadgeType.Error}
        />
      ),
    });
    if (c.resolution_type && c.resolution_type !== 'rejected') {
      rows.push({
        icon: 'note',
        label: label('claims.resolution', 'Resolution'),
        content: label(`claims.resolution_${c.resolution_type}`, c.resolution_type),
      });
    }
  }

  return (
    <div className="flex flex-col gap-y-32">
      <div className="flex flex-col gap-y-12">
        <h2 className="text-full typo-title-36">{c.name}</h2>
        <span className="text-faded typo-text-14">
          {t('case.created_on', { date: getFormattedDay(new Date(c.timestamp)) })}
        </span>
      </div>
      <Divider />
      <IconTable rows={rows} />

      {returnLinks.length > 0 && (
        <div className="flex flex-col">
          <OverlaySectionHeadline>{label('claims.col_return', 'Retoure')}</OverlaySectionHeadline>
          <LinkedReturnsTable returns={returnLinks} onReturnClick={returnId => navigate(`/returns/${returnId}`)} />
        </div>
      )}

      <div className="flex flex-col gap-y-16">
        <Divider className="mb-16" />
        <div className="flex flex-col gap-8">
          <span className="typo-text-14--bold text-faded">{t('general.description')}</span>
          <CaseDescription descriptionHtml={c.description || ''} />
        </div>
        {c.case_progress_note && (
          <div className="flex flex-col gap-8">
            <span className="typo-text-14--bold text-faded">
              {label('claims.case_progress_note', 'Notiz zum Fallverlauf')}
            </span>
            <span className="typo-text-14 text-full whitespace-pre-line">{c.case_progress_note}</span>
          </div>
        )}
        {c.status === 'documents_missing' && c.status_note && (
          <InfoCallout tone="warning" icon="warning">
            <span className="typo-text-14--bold">{label('claims.documents_required', 'Benötigte Unterlagen')}</span>
            <span className="whitespace-pre-line">{c.status_note}</span>
          </InfoCallout>
        )}
        {outcome && (
          <InfoCallout
            tone={outcome === 'accepted' ? 'success' : 'warning'}
            icon={outcome === 'accepted' ? 'checkmark' : 'warning'}>
            <span className="typo-text-14--bold">
              {label('claims.result', 'Ergebnis')}: {outcomeText}
            </span>
            {c.resolution_notes && <span className="whitespace-pre-line">{c.resolution_notes}</span>}
          </InfoCallout>
        )}
        {c.documents?.length > 0 && (
          <div className="flex flex-col gap-8 mt-8">
            <p className="typo-text-14--bold text-faded">{label('claims.documents', 'Documents')}</p>
            <div className="flex flex-wrap gap-16">
              {c.documents.map((doc: any) => (
                <div key={doc.id} className="self-start">
                  <Attachment
                    attachment={{ filename: doc.name || '', id: doc.id || 0, mimetype: doc.mimetype || '' }}
                    t={t}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        <Divider className="mt-16" />
      </div>

      <div className="flex flex-col gap-8">
        <Chat withCreateEditMessage model="df.claim" res_id={id} allowCase={false} />
      </div>
    </div>
  );
}

export function ClaimOverlay({ mode }: ClaimOverlayProps) {
  const { t } = useTranslation();
  const { id: idString } = useParams();
  const id = idString ? Number.parseInt(idString, 10) : undefined;

  const title = useMemo(
    () =>
      mode === 'create'
        ? t('claims.create_title', { defaultValue: 'Create claim' })
        : t('claims.title', { defaultValue: 'Claim' }),
    [mode, t],
  );

  let content: React.ReactNode = null;
  if (mode === 'create') {
    content = <ClaimCreateForm />;
  } else if (id) {
    content = <ClaimDetail id={id} />;
  }

  return (
    <RouteOverlay title={title}>
      <ContainerBody>{content}</ContainerBody>
    </RouteOverlay>
  );
}

export default ClaimOverlay;
